import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditFranchise = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [originalForm, setOriginalForm] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [form, setForm] = useState({
    name: '', primary: false, color: '#00FF00', number: '',
    general_inbox: '', street_address: '', street_address_2: '',
    city: '', state: '', zip: '',
    vehicle_daily_rate: '', tax_percentage: '',
    hourly_rate_low: '', hourly_rate_target: ''
  });

  useEffect(() => {
    const fetchFranchise = async () => {
      try {
        const res = await fetch(`${API_URL}/api/franchise-locations/${number}`);
        if (!res.ok) throw new Error();
        const { data } = await readResponse(res);
        const formData = {
          name: data.name || '',
          primary: data.primary || false,
          color: data.color || '#00FF00',
          number: data.number || '',
          general_inbox: data.general_inbox || '',
          street_address: data.street_address || '',
          street_address_2: data.street_address_2 || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          vehicle_daily_rate: data.vehicle_daily_rate || '',
          tax_percentage: data.tax_percentage || '',
          hourly_rate_low: data.hourly_rate_low || '',
          hourly_rate_target: data.hourly_rate_target || ''
        };
        setForm(formData);
        setOriginalForm(formData);
      } catch { toast.error('Failed to load franchise'); }
      finally { setLoading(false); }
    };
    fetchFranchise();
  }, [number]);

  const handleSave = async () => {
    // Re-entrancy guard against rapid double-click.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/franchise-locations/${number}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success('Record saved');
      setOriginalForm(form);
      setHasChanges(false);
    } catch { toast.error('Failed to save'); }
    finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const update = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    setHasChanges(JSON.stringify(next) !== JSON.stringify(originalForm));
    return next;
  });

  // Strip # for display, keep it for the color input
  const colorHex = form.color.replace('#', '').toUpperCase();

  if (loading) return <Layout><div className="p-8 text-center text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Edit Franchise</h1>
        </div>

        <div className="mb-6">
          <button
            onClick={() => navigate('/setup/franchise-locations')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
            data-testid="back-btn"
          >
            Back
          </button>
        </div>

        <div className="bg-white rounded shadow p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-5">

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">Franchise Name:</label>
              <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1 bg-gray-50" data-testid="franchise-name" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-gray-700 text-sm w-44 text-right font-medium">Primary:</label>
              <input type="checkbox" checked={form.primary} onChange={e => update('primary', e.target.checked)}
                className="w-5 h-5" data-testid="franchise-primary" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">Franchise Color:</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={e => update('color', e.target.value)}
                  className="w-32 h-10 cursor-pointer rounded border-0 p-0" data-testid="franchise-color" />
                <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">{colorHex}</span>
                <HelpCircle className="w-5 h-5 text-[#5B8DB8]" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-gray-700 text-sm w-44 text-right font-medium">Number:</label>
              <input type="text" value={form.number} onChange={e => update('number', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1 bg-gray-100" data-testid="franchise-number" readOnly />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-gray-700 text-sm w-44 text-right font-medium">General Inbox:</label>
              <input type="text" value={form.general_inbox} onChange={e => update('general_inbox', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-inbox" />
            </div>

            <div className="flex items-start gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium pt-2">Address:</label>
              <div className="flex-1 space-y-2">
                <input type="text" value={form.street_address} onChange={e => update('street_address', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="franchise-address1" />
                <input type="text" value={form.street_address_2} onChange={e => update('street_address_2', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="franchise-address2" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">City:</label>
              <input type="text" value={form.city} onChange={e => update('city', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-city" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">State:</label>
              <input type="text" value={form.state} onChange={e => update('state', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-state" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">Zip:</label>
              <input type="text" value={form.zip} onChange={e => update('zip', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-zip" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">Vehicle Day Rate:</label>
              <input type="text" value={form.vehicle_daily_rate} onChange={e => update('vehicle_daily_rate', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-vehicle-rate" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">
                <span>Tax Percentage</span>
                <span className="block text-xs text-red-400">(0-100)</span>
              </label>
              <input type="text" value={form.tax_percentage} onChange={e => update('tax_percentage', e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="franchise-tax" />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">
                <span>Hourly Rate</span>
                <span className="block text-xs text-red-400">LOW Value Alert</span>
              </label>
              <div className="flex items-center gap-2">
                <input type="text" value={form.hourly_rate_low} onChange={e => update('hourly_rate_low', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-32" data-testid="franchise-hourly-low" />
                <HelpCircle className="w-5 h-5 text-[#5B8DB8]" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-400 italic text-sm w-44 text-right font-medium">
                <span>Hourly Rate</span>
                <span className="block text-xs text-red-400">TARGET Value</span>
              </label>
              <div className="flex items-center gap-2">
                <input type="text" value={form.hourly_rate_target} onChange={e => update('hourly_rate_target', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-32" data-testid="franchise-hourly-target" />
                <HelpCircle className="w-5 h-5 text-[#5B8DB8]" />
              </div>
            </div>

            <div className="flex justify-center pt-4">
              <button onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`px-8 py-2 rounded border transition-colors ${
                  hasChanges && !saving
                    ? 'bg-green-500 hover:bg-green-600 text-white border-green-600'
                    : 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed'
                }`}
                data-testid="save-record-btn"
              >
                {saving ? 'Saving…' : 'Save Record'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditFranchise;
